/**
 * Lane SCENE-BUILDINGS (batch 2026-10-13): 25 more street, farm, shrine and garden pieces for the plains kit, built
 * with the same helpers as props.ts / props2.ts (propPix, box, blob, K / M ramps). Original designs. Audit: none of
 * these ids existed in props*.ts / stalls / carpet. Each is an upright card anchored at its foot (see SOLID4 for the
 * collision footprint, in tiles, beside every mesh) or a flat decal (walkable, sorted at its far corner like carpets).
 * Switched on by one spread in props.ts (MAKERS4 / PropId4); place with kit/placement.ts like any prop.
 *
 * Polish pass (2026-10-13, ns-pixel): lifted to the houses' level: the houses' own curved tile roofs, ridge lines,
 * corner tassels and finials on the roofed pieces; boxes with a lit top lip, a lit near corner and a dark foot (AO);
 * plank, rock and brick textures with grain, chips and moss; round things (millstones, drums, stools, gong) as lit
 * cylinders in stepped form-light clusters; a baked contact shadow (cool, solid core + dithered rim, like
 * contactShadow.ts) under each upright. Every sprite is still painted once and cached by propSprite.
 */
import { hash2, valueNoise } from '@nine/shared';
import { M, quad, roof, snap, top, wall, type Pt, type Ramp, type RGB, type Tex } from '../buildings';
import type { Sprite } from '../scenery';
import { blob, column, cornerTassels, eaveShadow, finial, K, keyBand, propPix, roofEdges, tassel } from './parts';
import { barrelAt, fin, pole, type Pp } from './propBase';
import type { FlatSprite } from './carpet';

const rp = (...hex: string[]): Ramp => hex.map((h) => [parseInt(h.slice(1, 3), 16), parseInt(h.slice(3, 5), 16), parseInt(h.slice(5, 7), 16)] as const);
const LEAF = rp('#12281a', '#1b3a22', '#285030', '#386a3c', '#52874a', '#7aa862');
const PINEG = rp('#0c1f1a', '#123028', '#1a4436', '#255c48', '#35765c');
const BLOOM = rp('#5a2a3a', '#8e4458', '#bf6c82', '#e096aa', '#f6c8d2');
const WILLOW = rp('#1c3a1c', '#2c5224', '#437034', '#62904a', '#8cb468');
const CLOTH = rp('#3e1418', '#6e2420', '#9c3a26', '#c45c34', '#e08a52');
/** Bronze (gong, sundial gnomon): deep brown shadow to a pale warm gold. */
const BRONZE = rp('#2e1a0c', '#5a3414', '#8a5a22', '#b8862e', '#dcb050', '#f6e2a0');
/** Fire (forge coals): cinder to white-hot. */
const FIRE = rp('#3a1008', '#7a1e0c', '#c0401a', '#f07a2a', '#ffc45a', '#fff0b0');
/** Pig hide, pigeon grey, tea leaf. */
const PIG = rp('#3e2224', '#6e3e3a', '#a0625a', '#c88a7a', '#e6b4a0');
const DOVE = rp('#2e3040', '#545868', '#8a90a0', '#b8bcc8', '#e4e6ea');
const TEA = rp('#14261a', '#1f3c24', '#2f5a2e', '#4a7a3a', '#6e9a48');
const BAMBOO = rp('#1a3a1a', '#2a5a24', '#3f7a30', '#5a9a42', '#86bc5e');

/** Collision footprint (tiles, centred on the anchor) for each upright prop; flat decals are not listed (walkable). */
export const SOLID4: Record<string, readonly [number, number]> = {
  noticeBoard: [1, 0.4], waterTrough: [1.4, 0.5], millstone: [1, 1], scarecrow: [0.3, 0.3], henCoop: [1, 1],
  farmGateX: [2, 0.3], farmGateY: [0.3, 2], stoneTable: [1.4, 1.4], forge: [1.4, 1], wishingTree: [0.6, 0.6],
  prayerWheel: [0.6, 0.6], sundial: [0.8, 0.8], dovecote: [0.5, 0.5], palanquin: [1.6, 1], teaTrays: [1.6, 1],
  waterWheel: [1, 2], pigPen: [2, 2], woodShed: [2, 1], gongStand: [1.2, 0.4], willowTree: [0.6, 0.6],
  plumTree: [0.5, 0.5], pineTree: [0.5, 0.5], bambooArch: [2, 0.4],
};

// ───────────────────────── shared painters ─────────────────────────

const SHADOW: RGB = [22, 20, 40];
/**
 * The kit finish, then a contact shadow on the empty pixels round the foot: the footprint rect (`hx` × `hy` tiles
 * half-size) grown by `m`, a solid cool core and a dithered rim (every other pixel), as contactShadow.ts draws them.
 * `inner` > 0 keeps only a ring that far inside the rect (pens, open frames).
 */
function fin4(p: Pp, hx = 0, hy = 0, m = 0.16, inner = 9): Sprite {
  const s = fin(p);
  if (hx <= 0) return s;
  const { w, h, d } = p.px;
  for (let Y = 0; Y < h; Y++)
    for (let X = 0; X < w; X++) {
      const i = (Y * w + X) * 4;
      if (d[i + 3]) continue;
      const a = (X + 0.5 - p.ax) / 16;
      const b = (Y + 0.5 - p.ay) / 8;
      const e = Math.max(Math.abs((a + b) / 2) - hx, Math.abs((b - a) / 2) - hy);
      if (e > m || e < -inner) continue;
      const core = e < m * 0.4;
      if (!core && (X + Y) & 1) continue;
      d[i] = SHADOW[0];
      d[i + 1] = SHADOW[1];
      d[i + 2] = SHADOW[2];
      d[i + 3] = core ? 110 : 76;
    }
  return { img: p.px.toCanvas(), ax: p.ax, ay: p.ay };
}

/** A box with the kit's edge work: a lit 1-px lip along the top of each wall, a lit near corner, a dark foot (AO). */
function bx(p: Pp, r: readonly [number, number, number, number], z0: number, z1: number, side: Tex, topT: Tex = side, ao = z0 === 0): void {
  const [x0, y0, x1, y1] = r;
  const e = (v: number): number => (v >= z1 - 1 ? 0.2 : 0) + (ao && v < z0 + 2 ? -0.16 : 0);
  wall(p.px, p.P, 'left', y1, x0, x1, z0, z1, (u, v, l) => side(u, v, l + e(v)));
  wall(p.px, p.P, 'right', x1, y0, y1, z0, z1, (u, v, l) => side(u, v, l + e(v) + (u >= y1 * 16 - 1.2 ? 0.12 : 0)));
  top(p.px, p.P, r, z1, (u, v, l) => topT(u, v, l + (u >= x1 * 16 - 1.2 || v >= y1 * 16 - 1.2 ? 0.14 : 0)));
}

const md = (a: number, n: number): number => ((a % n) + n) % n;
/** Planks: a dark seam every `pw` px (across u for upright boards, up v for laid ones), a shade per board, grain. */
const planks =
  (r: Ramp = M.wood, l0 = 0, pw = 5, upright = true): ((u: number, v: number, l: number) => RGB) =>
  (u, v, l) => {
    const U = u | 0;
    const V = v | 0;
    const a = upright ? u : v;
    const b = upright ? v : u;
    const k = Math.floor(a / pw);
    if (md(Math.floor(a), pw) === 0) return r[1]!;
    let q = l + l0 + (hash2(k, 7) - 0.5) * 0.18;
    if (hash2(Math.floor(b / 3) + k * 13, Math.floor(a)) > 0.88) q -= 0.13;
    return snap(r, q, U, V, 0.12);
  };
/** Rough stone: mottled in broad patches, chips and flecks, moss creeping up the first px when `moss`. */
const rock =
  (r: Ramp = M.stone, l0 = 0, moss = false): ((u: number, v: number, l: number) => RGB) =>
  (u, v, l) => {
    const U = u | 0;
    const V = v | 0;
    let q = l + l0 + (valueNoise(U / 5 + 3, V / 3.5) - 0.5) * 0.16;
    const h = hash2(U * 3 + 1, V * 5 + 2);
    if (h > 0.95) q -= 0.17;
    else if (h < 0.035) q += 0.13;
    if (moss && v < 3 && valueNoise(U / 3, 9) > 0.52) return snap(M.moss, 0.3 + l * 0.4, U, V, 0.3);
    return snap(r, q, U, V, 0.15);
  };
const stoneAt = (l: number, X: number, Y: number, r: Ramp = M.stone): RGB => rock(r)(X, Y, l);
/** Dark granite (runner stones, stools): bluer and deeper than the apron stone so stacked pieces part. */
const GRANITE = rp('#2a2a34', '#42414c', '#5c5960', '#787370', '#958f86', '#b2aa9c');

/** Fired brick, warm grey, 3-px courses offset by half a brick, dark mortar, each brick lit along its top. */
const brk =
  (l0 = 0): ((u: number, v: number, l: number) => RGB) =>
  (u, v, l) => {
    const row = Math.floor(v / 4);
    const off = row & 1 ? 4 : 0;
    const U = u | 0;
    const V = v | 0;
    if (md(Math.floor(v), 4) === 0 || md(Math.floor(u) + off, 8) === 0) return snap(M.brick, l - 0.3 + l0, U, V, 0);
    const b = Math.floor((u + off) / 8);
    const q = l + l0 + (hash2(b * 7, row * 13) - 0.5) * 0.22 + (md(Math.floor(v), 4) === 3 ? 0.1 : 0);
    return snap(hash2(b, row) > 0.9 ? M.terra : M.brick, q, U, V, 0.1);
  };

/** Stepped form light across a round thing, lit from the right: shadow core, half tone, light, a lit rim band. */
const form = (a: number): number => (a > 0.62 ? 0.76 : a > 0.12 ? 0.6 : a > -0.5 ? 0.44 : 0.28);
/**
 * An upright cylinder standing on the screen ellipse (cx, cy) rx × ry, h px tall: `side(a, t, X, Y)` paints the wall
 * (a: −1 left … 1 right, t: 0 foot … 1 top), `cap(a, b, X, Y)` the top (a, b: −1 … 1 across the ellipse).
 */
function cyl(
  p: Pp,
  cx: number,
  cy: number,
  rx: number,
  ry: number,
  h: number,
  side: (a: number, t: number, X: number, Y: number) => RGB | null,
  cap: ((a: number, b: number, X: number, Y: number) => RGB | null) | null,
): void {
  const R = Math.round(rx);
  for (let x = -R; x <= R; x++) {
    const a = x / (rx + 0.5);
    const yb = Math.round(cy + ry * Math.sqrt(Math.max(0, 1 - a * a)));
    for (let z = 0; z <= h; z++) {
      const c = side(a, h ? z / h : 0, cx + x, yb - z);
      if (c) p.px.set(cx + x, yb - z, c);
    }
  }
  if (cap)
    for (let b = -Math.ceil(ry); b <= Math.ceil(ry); b++)
      for (let x = -Math.ceil(rx); x <= Math.ceil(rx); x++) {
        if ((x * x) / (rx * rx + 0.01) + (b * b) / (ry * ry + 0.01) > 1) continue;
        const c = cap(x / rx, b / ry, cx + x, cy - h + b);
        if (c) p.px.set(cx + x, cy - h + b, c);
      }
}
/** Cap light: lit toward the right, a lit front rim (the edge that catches the sky). */
const capL = (a: number, b: number): number => 0.62 + a * 0.12 - b * 0.04 + (a * a + b * b > 0.7 && b > 0 ? 0.14 : 0);

/** A 1-px line between screen points. */
function ln(p: Pp, a: Pt, b: Pt, c: RGB | ((f: number) => RGB)): void {
  const n = Math.max(1, Math.ceil(Math.max(Math.abs(b[0] - a[0]), Math.abs(b[1] - a[1]))));
  for (let k = 0; k <= n; k++) {
    const f = k / n;
    p.px.set(Math.round(a[0] + (b[0] - a[0]) * f), Math.round(a[1] + (b[1] - a[1]) * f), typeof c === 'function' ? c(f) : c);
  }
}
/** A 2-px rail between ground points at height z: a lit top pixel over a shaded one. */
function railLn(p: Pp, a: readonly [number, number], b: readonly [number, number], z: number, r: Ramp = M.wood): void {
  const A = p.P(a[0], a[1], z);
  const B = p.P(b[0], b[1], z);
  ln(p, [A[0], A[1] + 1], [B[0], B[1] + 1], (f) => r[hash2(Math.round(f * 40), 3) > 0.85 ? 1 : 2]!);
  ln(p, A, B, (f) => r[hash2(Math.round(f * 40), 5) > 0.8 ? 3 : 4]!);
}
/** A round post (2–3 px), lit right, a lit cut top. */
function post(p: Pp, x: number, y: number, z0: number, z1: number, r: Ramp = M.wood, w = 3): void {
  const [sx, sy] = p.P(x, y, 0);
  for (let z = z0; z < z1; z++)
    for (let a = 0; a < w; a++) {
      const l = a === w - 1 ? 0.7 : a === 0 ? 0.32 : 0.5;
      p.px.set(sx - (w >> 1) + a, sy - z, snap(r, l + (z >= z1 - 1 ? 0.18 : 0) + (z < 2 ? -0.12 : 0) + (hash2(a, z >> 1) > 0.86 ? -0.12 : 0), a, z, 0));
    }
}

/** A small hip roof with the houses' curved tiles, lit edges and (optionally) corner tassels and a finial. */
function smallRoof(p: Pp, eaves: readonly [number, number, number, number], ze: number, ridge: readonly [number, number, number, number], zt: number, tiles: Ramp, opts: { tassels?: boolean; fin?: number; shadow?: number } = {}): void {
  if (opts.shadow) eaveShadow(p.px, p.P, eaves, ze, opts.shadow);
  roof(p.px, p.P, eaves, ze, ridge, zt, tiles, M.stone, false);
  roofEdges(p.px, p.P, eaves, ze, ridge, zt, tiles);
  // A slim ridge at this size (the houses' 5-px ridge and 7-px horns would swamp it): a dark cap, a lit top line,
  // small curled ends with a gold fleck.
  if (ridge[0] !== ridge[2] || ridge[1] !== ridge[3]) {
    const a = p.P(ridge[0], ridge[1], zt);
    const b = p.P(ridge[2], ridge[3], zt);
    ln(p, [a[0], a[1] + 1], [b[0], b[1] + 1], tiles[0]!);
    ln(p, a, b, tiles[1]!);
    ln(p, [a[0], a[1] - 1], [b[0], b[1] - 1], tiles[tiles.length - 1]!);
    for (const [q, d] of [[a, -1], [b, 1]] as const) {
      p.px.set(q[0] + d, q[1] - 2, tiles[2]!);
      p.px.set(q[0] + d * 2, q[1] - 3, tiles[tiles.length - 1]!);
      p.px.set(q[0], q[1] - 2, M.gold[3]!);
    }
  }
  if (opts.tassels) cornerTassels(p.px, p.P, eaves, ze);
  if (opts.fin) finial(p.px, p.P((ridge[0] + ridge[2]) / 2, (ridge[1] + ridge[3]) / 2, zt), opts.fin);
}

/** A small clump of straw: a fan of 5 stalks, lit tips. */
function tuft(p: Pp, x: number, y: number): void {
  for (const [dx, h] of [[-2, 2], [-1, 3], [0, 4], [1, 3], [2, 2]] as const)
    for (let k = 0; k < h; k++) p.px.set(x + dx + (k === h - 1 ? Math.sign(dx) : 0), y - k, K.straw[k === h - 1 ? 4 : k ? 3 : 1]!);
}

// ───────────────────────── little creatures ─────────────────────────

/** `bob`: the head dipped this many px (a peck; propAnim4 loops it). */
function hen(p: Pp, x: number, y: number, dir: 1 | -1, brown = false, bob = 0): void {
  const body = brown ? K.ochre : M.linen;
  p.px.set(x - 1, y, M.gold[2]!);
  p.px.set(x + 1, y, M.gold[2]!);
  blob(p.px, x, y - 3, 2.6, 2, body, 0.55);
  p.px.set(x - dir * 3, y - 5, body[2]!);
  p.px.set(x - dir * 3, y - 6, body[3]!);
  p.px.set(x - dir * 2, y - 6, body[1]!);
  const hy = y + bob;
  const hx = x + (bob > 0 ? dir : 0);
  p.px.rect(hx + dir * 2 - (dir < 0 ? 1 : 0), hy - 7, 2, 2, body[4]!);
  p.px.set(hx + dir * 2, hy - 8, M.red[3]!);
  p.px.set(hx + dir * 2 + (dir > 0 ? 1 : -1), hy - 8, M.red[2]!);
  p.px.set(hx + dir * 4, hy - 6, M.gold[3]!);
  p.px.set(hx + dir * 3, hy - 7, M.dark[0]!);
  p.px.set(hx + dir * 2, hy - 5, M.red[2]!);
}
/** `nod`: the head lowered this many px (rooting; propAnim4 loops it). */
function pig(p: Pp, x: number, y: number, dir: 1 | -1, s = 1, nod = 0): void {
  const rx = 6 * s;
  for (const k of [-0.6, -0.15, 0.25, 0.65]) p.px.rect(Math.round(x + k * rx), y - 1, 1, 2, PIG[1]!);
  blob(p.px, x, y - 3.5 * s - 1, rx, 3.4 * s, PIG, 0.56);
  // a lit ridge along the back, a mud splash on the flank
  for (let a = -Math.round(rx * 0.6); a <= Math.round(rx * 0.5); a++) p.px.set(x + a, y - Math.round(6.6 * s) - 1, PIG[4]!);
  p.px.rect(x - dir * 2, y - 3, 3, 1, K.mud[2]!);
  p.px.set(x - dir * 1, y - 4, K.mud[3]!);
  const hx = x + dir * Math.round(rx + 1);
  const hy = y + nod;
  blob(p.px, hx, hy - 4, 2.8 * s, 2.5 * s, PIG, 0.6);
  p.px.rect(hx + dir * Math.round(2 * s) - (dir < 0 ? 1 : 0), hy - 4, 2, 2, PIG[4]!);
  p.px.set(hx + dir * Math.round(2 * s) + (dir > 0 ? 1 : 0), hy - 3, PIG[1]!);
  p.px.set(hx, hy - 5, M.dark[0]!);
  p.px.set(hx - dir, hy - 7, PIG[1]!);
  p.px.set(hx - dir * 2, hy - 7, PIG[2]!);
  p.px.set(x - dir * Math.round(rx + 1), y - 5, PIG[3]!);
  p.px.set(x - dir * Math.round(rx + 2), y - 6, PIG[2]!);
}
function pigeon(p: Pp, x: number, y: number, dir: 1 | -1): void {
  blob(p.px, x, y - 2, 2.2, 1.5, DOVE, 0.55);
  p.px.set(x + dir * 2, y - 4, DOVE[3]!);
  p.px.set(x + dir * 2, y - 3, DOVE[2]!);
  p.px.set(x + dir * 3, y - 4, M.gold[2]!);
  p.px.set(x - dir * 3, y - 2, DOVE[1]!);
  p.px.set(x - dir * 1, y - 2, DOVE[1]!);
}

// ───────────────────────── trees (kept; minor touch) ─────────────────────────

const fol = (p: Pp, cx: number, cy: number, rx: number, ry: number, r: Ramp, seed: number): void => {
  blob(p.px, cx, cy, rx, ry, r, 0.5);
  for (let k = 0; k < 6; k++) {
    const a = hash2(seed + k, 3) * 6.28;
    blob(p.px, cx + Math.cos(a) * rx * 0.6, cy + Math.sin(a) * ry * 0.55, rx * 0.5, ry * 0.5, r, 0.45 + hash2(seed, k) * 0.35);
  }
};
const trunk = (p: Pp, x: number, h: number, w: number, lean = 0): void => {
  for (let z = 0; z < h; z++) {
    const cx = x + Math.round((z / h) * lean);
    for (let a = 0; a < w; a++) p.px.set(cx + a - (w >> 1), p.ay - z, snap(M.wood, 0.25 + (a / w) * 0.5 + (hash2(a, z >> 1) > 0.8 ? -0.15 : 0), a, z, 0.2));
  }
  // root flare at the foot
  p.px.rect(x - (w >> 1) - 1, p.ay - 1, w + 2, 2, M.wood[1]!);
  p.px.set(x + (w >> 1) + 1, p.ay - 2, M.wood[2]!);
};

// ───────────────────────── the pieces ─────────────────────────

function noticeBoard(): Sprite {
  const p = propPix(64, 80, 32, 64);
  for (const x of [-0.46, 0.46]) {
    bx(p, [x - 0.1, -0.1, x + 0.1, 0.1], 0, 3, rock(M.stone, 0, true), rock());
    pole(p.P, p, x, 0, 0.055, 3, 44, M.wood);
  }
  bx(
    p,
    [-0.5, -0.035, 0.5, 0.035],
    14,
    38,
    (u, v, l) => (Math.abs(u) > 6.6 || v < 15.2 || v > 36.6 ? snap(M.timber, l + 0.2, u | 0, v | 0, 0.1) : planks(M.deck, -0.12, 5, false)(u, v, l)),
    (u, v, l) => snap(M.timber, l, u | 0, v | 0, 0.1),
  );
  // Notices: cream and buff sheets with ink lines and a red pin, one red decree with gold strokes.
  const papers = [
    [-0.27, 0.15, 27, 35, K.cream, 1],
    [0.06, 0.12, 28, 35, M.red, 4],
    [0.3, 0.1, 26, 34, K.cream, 3],
    [-0.22, 0.12, 17, 24, M.paper, 2],
    [0.14, 0.18, 17, 25, K.cream, 5],
  ] as const;
  for (const [x, hw, z0, z1, c, s] of papers) {
    wall(
      p.px,
      p.P,
      'left',
      0.045,
      x - hw,
      x + hw,
      z0,
      z1,
      (u, v) => {
        const lu = u - (x - hw) * 16;
        const w = hw * 32;
        const tv = z1 - v;
        if (tv < 1.2 && Math.abs(lu - w / 2) < 0.7) return M.red[3]!;
        const red = c === M.red;
        const ink = lu > 1.4 && lu < w - 1.4 && tv > 2.4 && tv < z1 - z0 - 1 && (v | 0) % 2 === 0 && hash2((lu | 0) + s * 31, v | 0) > 0.28;
        if (ink) return red ? M.gold[4]! : M.dark[2]!;
        return snap(c, (red ? 0.55 : 0.68) + (tv < 1.2 ? 0.15 : 0) + (lu < 1 ? -0.18 : 0) + (lu > w - 1 && tv < 3 ? 0.2 : 0), u | 0, v | 0, 0);
      },
      0.6,
    );
  }
  smallRoof(p, [-0.66, -0.2, 0.66, 0.2], 44, [-0.4, 0, 0.4, 0], 53, K.greyTile, { tassels: true, shadow: 6 });
  return fin4(p, 0.55, 0.14);
}

function waterTrough(): Sprite {
  const p = propPix(72, 44, 36, 28);
  for (const x of [-0.5, 0.5]) bx(p, [x - 0.12, -0.25, x + 0.12, 0.25], 0, 3, rock(M.stone, 0, true), rock());
  const ox = 0.7;
  const oy = 0.22;
  bx(
    p,
    [-ox, -oy, ox, oy],
    3,
    13,
    (u, v, l) => {
      if (l < 0.6 && (Math.abs(u + 6.5) < 1 || Math.abs(u - 6.5) < 1)) return snap(K.iron, l + (v > 11 ? 0.3 : 0.05), u | 0, v | 0, 0);
      if (l < 0.6 && (Math.abs(u + 6.5) < 1.6 || Math.abs(u - 6.5) < 1.6) && (v | 0) % 3 === 1) return K.iron[4]!;
      return planks(M.wood, 0, 4, false)(u, v, l);
    },
    (u, v, l) => {
      if (Math.abs(u) < ox * 16 - 1.6 && Math.abs(v) < oy * 16 - 1.4) {
        const far = v < -oy * 16 + 2.6;
        const glint = hash2(u | 0, v | 0) > 0.93 || ((u | 0) % 7 === 0 && Math.abs(v) < 0.6);
        return far ? M.blue[1]! : glint ? M.blue[4]! : snap(M.blue, 0.42 + (u / 32) * 0.3, u | 0, v | 0, 0.3);
      }
      return planks(M.wood, 0.05, 4, false)(u, v, l);
    },
    false,
  );
  // ladle over the rim, a wet patch and hay strands at the foot
  const a = p.P(0.25, -0.05, 14);
  ln(p, a, [a[0] + 9, a[1] - 5], M.wood[3]!);
  blob(p.px, a[0] - 1, a[1] + 1, 2, 1.2, M.wood, 0.5);
  const [wx, wy] = p.P(0.62, 0.32, 0);
  tuft(p, wx + 3, wy);
  return fin4(p, 0.72, 0.26);
}

function millstone(): Sprite {
  const p = propPix(60, 52, 30, 36);
  const { ax, ay } = p;
  // Base: a round stone apron with a groove and a spout.
  cyl(
    p,
    ax,
    ay,
    15,
    7.5,
    5,
    (a, t, X, Y) => (t < 0.3 && valueNoise(X / 3, 9) > 0.55 ? snap(M.moss, 0.4, X, Y, 0.3) : stoneAt(form(a) - (t < 0.25 ? 0.1 : 0) + (t > 0.85 ? 0.12 : 0), X, Y)),
    (a, b, X, Y) => {
      const r = a * a + b * b;
      if (r > 0.62 && r < 0.78) return stoneAt(0.34, X, Y);
      return stoneAt(capL(a, b) - 0.05, X, Y);
    },
  );
  const [sx, sy] = [ax + 13, ay - 2];
  p.px.rect(sx, sy, 4, 2, M.stone[2]!);
  p.px.rect(sx, sy - 1, 4, 1, M.stone[4]!);
  // Bed stone and runner stone, the runner's top dressed with harp grooves round a feed eye.
  cyl(p, ax, ay - 6, 12, 6, 3, (a, t, X, Y) => stoneAt(form(a) - 0.1 + (t < 0.4 ? -0.18 : 0), X, Y), null);
  p.px.rect(ax - 11, ay - 6, 23, 1, M.stone[1]!);
  cyl(
    p,
    ax,
    ay - 9,
    11,
    5.5,
    6,
    (a, t, X, Y) => stoneAt(form(a) + 0.04 + (t > 0.8 ? 0.14 : t < 0.2 ? -0.24 : 0) + (hash2(X, 3) > 0.9 ? -0.15 : 0), X, Y, GRANITE),
    (a, b, X, Y) => {
      const r = Math.hypot(a, b);
      if (r < 0.2) return M.dark[1]!;
      if (r < 0.3) return hash2(X, Y) > 0.5 ? K.straw[3]! : stoneAt(0.42, X, Y);
      const ang = Math.atan2(b, a) + Math.PI;
      const sec = md(ang / (Math.PI / 4) + r * 1.2, 1);
      if (sec < 0.14 && r < 0.9) return GRANITE[2]!;
      return stoneAt(capL(a, b), X, Y, GRANITE);
    },
  );
  // Peg and push arm.
  for (let z = 0; z < 6; z++) p.px.rect(ax + 6, ay - 16 - z, 2, 1, M.wood[z === 5 ? 4 : 3]!);
  ln(p, [ax + 6, ay - 22], [ax + 24, ay - 30], M.wood[4]!);
  ln(p, [ax + 6, ay - 21], [ax + 24, ay - 29], M.wood[2]!);
  p.px.rect(ax + 23, ay - 31, 2, 3, M.wood[1]!);
  // spilled flour
  p.px.rect(ax + 12, ay - 2, 3, 1, K.cream[4]!);
  p.px.rect(ax + 11, ay - 1, 5, 1, K.cream[3]!);
  return fin4(p, 0.5, 0.5);
}

function scarecrow(): Sprite {
  const p = propPix(52, 80, 26, 68);
  const { ax, ay } = p;
  // pole and crossbar
  for (let z = 0; z < 50; z++) for (let a = -1; a <= 1; a++) p.px.set(ax + a, ay - z, snap(M.wood, a === 1 ? 0.62 : a ? 0.3 : 0.45, a, z, 0));
  p.px.rect(ax - 16, ay - 40, 33, 1, M.wood[4]!);
  p.px.rect(ax - 16, ay - 39, 33, 1, M.wood[2]!);
  // robe: a ragged trapezoid, shade cluster on the left, fold at the waist, an indigo patch
  for (let y = ay - 41; y < ay - 19; y++) {
    const t = (y - (ay - 41)) / 22;
    const hw = Math.round(5 + t * 3);
    const hem = y > ay - 22;
    for (let x = -hw; x <= hw; x++) {
      if (hem && hash2(x + 9, 2) > 0.6 && y > ay - 21) continue;
      let l = x < -hw + 3 ? 0.32 : x > hw - 2 ? 0.66 : 0.5;
      if (x === -1 && t > 0.5) l -= 0.14;
      if (x === 2 && t > 0.55) l += 0.12;
      if (y === ay - 31 || y === ay - 30) {
        p.px.set(ax + x, y, K.straw[y === ay - 31 ? 4 : 2]!);
        continue;
      }
      p.px.set(ax + x, y, snap(CLOTH, l, x, y, 0));
    }
  }
  for (let y = 0; y < 4; y++) for (let x = 0; x < 4; x++) p.px.set(ax + 1 + x, ay - 27 + y, x === 3 || y === 0 ? K.indigo[3]! : K.indigo[2]!);
  p.px.set(ax + 1, ay - 27, M.linen[3]!);
  p.px.set(ax + 4, ay - 24, M.linen[3]!);
  // sleeves draped on the bar, straw out of the cuffs and hem
  for (const s of [-1, 1]) {
    for (let k = 4; k < 14; k++) {
      const drop = 3 + Math.round(Math.sin(k * 0.7) * 1) + (k > 10 ? 1 : 0);
      for (let d = 0; d < drop; d++) p.px.set(ax + s * k, ay - 40 + d, snap(CLOTH, (s > 0 ? 0.6 : 0.42) - d * 0.06, k, d, 0));
    }
    for (let k = 0; k < 4; k++) p.px.rect(ax + s * (13 + (k & 1)), ay - 37 + (k >> 1), 1, 3 + (k & 1), K.straw[2 + (k & 1) + (s > 0 ? 1 : 0)]!);
  }
  for (let k = 0; k < 7; k++) p.px.rect(ax - 6 + k * 2, ay - 19, 1, 2 + (k % 3), K.straw[3 + (k & 1)]!);
  // burlap head with a stitched face, a rope tie
  blob(p.px, ax, ay - 46, 4.5, 4.6, K.cream, 0.48);
  for (const e of [-2, 2]) {
    p.px.set(ax + e - 1, ay - 48, M.dark[1]!);
    p.px.set(ax + e, ay - 47, M.dark[1]!);
    p.px.set(ax + e + 1, ay - 48, M.dark[1]!);
  }
  p.px.rect(ax - 2, ay - 44, 5, 1, K.cream[0]!);
  p.px.set(ax - 1, ay - 45, K.cream[0]!);
  p.px.set(ax + 1, ay - 45, K.cream[0]!);
  p.px.rect(ax - 3, ay - 42, 7, 1, K.straw[2]!);
  // conical straw hat: woven rays, a lit right side, a dark brim underside
  for (let k = 0; k < 7; k++) {
    const w = 2 + k * 1.6;
    for (let x = -Math.round(w); x <= Math.round(w); x++) {
      const l = 0.48 + (x / w) * 0.22 + ((x + k) % 3 === 0 ? -0.12 : 0);
      p.px.set(ax + x, ay - 57 + k, snap(K.straw, l, x, k, 0));
    }
  }
  p.px.rect(ax - 11, ay - 50, 23, 1, K.straw[1]!);
  p.px.set(ax, ay - 58, K.straw[4]!);
  // a crow on the bar's end
  blob(p.px, ax + 14, ay - 43, 2.6, 1.8, K.indigo.map((c) => [c[0] * 0.5, c[1] * 0.5, c[2] * 0.6] as const), 0.5);
  p.px.rect(ax + 16, ay - 46, 2, 2, M.dark[1]!);
  p.px.set(ax + 18, ay - 45, M.gold[3]!);
  p.px.set(ax + 12, ay - 44, M.dark[0]!);
  p.px.set(ax + 11, ay - 45, M.dark[0]!);
  return fin4(p, 0.18, 0.18, 0.16);
}

/** `k`: the loop's frame (propAnim4): the hens peck in turn. */
function henCoop(k = 0): Sprite {
  const p = propPix(68, 64, 32, 46);
  for (const x of [-0.36, 0.36]) for (const y of [-0.36, 0.36]) pole(p.P, p, x, y, 0.04, 0, 8, M.timber);
  bx(p, [-0.38, -0.38, 0.38, 0.38], 8, 22, planks(M.wood, 0.02, 4, true), (u, v, l) => snap(K.thatch, l, u | 0, v | 0), true);
  // window slats on the left face, the door on the right with a hen looking out
  wall(p.px, p.P, 'left', 0.385, -0.2, 0.12, 13, 19, (u, v) => (md(Math.floor(u), 2) === 0 ? M.wood[4]! : v > 17 ? M.dark[0]! : M.dark[1]!), 0.5);
  wall(p.px, p.P, 'right', 0.385, -0.12, 0.16, 9, 18, (u, v) => (v < 10.5 ? K.straw[3]! : u > 1.6 ? M.wood[4]! : M.dark[0]!), 0.7);
  const [dx, dy] = p.P(0.39, 0.0, 12);
  p.px.rect(dx, dy - 2, 2, 2, M.linen[4]!);
  p.px.set(dx + 1, dy - 3, M.red[3]!);
  p.px.set(dx + 2, dy - 1, M.gold[3]!);
  smallRoof(p, [-0.52, -0.52, 0.52, 0.52], 22, [-0.18, 0, 0.18, 0], 32, K.thatch, { shadow: 6 });
  // the hen ramp: a plank with cleats, lit along its top
  quad(p.px, p.P(0.98, 0.12, 0), p.P(0.98, -0.1, 0), p.P(0.4, -0.1, 9), p.P(0.4, 0.12, 9), (s2, t) => {
    if (md(Math.round(t), 3) === 0) return M.wood[1]!;
    return snap(M.wood, 0.62 + (s2 < 1 ? -0.2 : 0), Math.round(s2), Math.round(t), 0);
  });
  const [h1x, h1y] = p.P(0.55, 0.55, 0);
  hen(p, h1x, h1y, -1, false, [0, 1, 2, 1, 0, 0][k % 6]!);
  const [h2x, h2y] = p.P(-0.1, 0.62, 0);
  hen(p, h2x, h2y, 1, true, [0, 0, 0, 1, 2, 1][k % 6]!);
  tuft(p, h2x - 9, h2y + 1);
  return fin4(p, 0.45, 0.45);
}

function farmGate(axis: 'x' | 'y'): Sprite {
  const p = propPix(84, 56, 42, 42);
  const at = (t: number): readonly [number, number] => (axis === 'x' ? [t, 0] : [0, t]);
  // the rails first (posts stand in front of their ends)
  for (const z of [7, 14, 21]) railLn(p, at(-0.95), at(0.95), z);
  const d0 = p.P(...at(-0.9), 7);
  const d1 = p.P(...at(0.9), 21);
  ln(p, d0, d1, M.wood[3]!);
  ln(p, [d0[0], d0[1] + 1], [d1[0], d1[1] + 1], M.wood[1]!);
  for (const t of [-1, 1]) {
    const [x, y] = at(t);
    bx(p, [x - 0.08, y - 0.08, x + 0.08, y + 0.08], 0, 2, rock(M.stone, 0, true), rock());
    post(p, x, y, 2, 27, M.wood, 3);
    const [cx, cy] = p.P(x, y, 27);
    p.px.set(cx, cy - 1, M.wood[4]!);
    // rope hinge loops
    for (const z of [9, 19]) {
      const [hx, hy] = p.P(x, y, z);
      p.px.rect(hx - 2, hy, 5, 1, K.straw[3]!);
      p.px.set(hx + 2, hy + 1, K.straw[1]!);
    }
  }
  return fin4(p, axis === 'x' ? 1 : 0.1, axis === 'x' ? 0.1 : 1, 0.12);
}

function stoneTable(): Sprite {
  const p = propPix(80, 62, 40, 42);
  const { ax, ay } = p;
  const stool = (x: number, y: number): void => {
    const [sx, sy] = p.P(x, y, 0);
    cyl(
      p,
      sx,
      sy,
      4.5,
      2.3,
      8,
      (a, t, X, Y) => stoneAt(form(a) + (t > 0.3 && t < 0.42 ? -0.16 : 0) + (t > 0.85 ? 0.16 : 0) + (t < 0.15 ? -0.1 : 0), X, Y, GRANITE),
      (a, b, X, Y) => stoneAt(capL(a, b), X, Y, GRANITE),
    );
  };
  stool(-0.74, 0.24);
  // table: a waisted pedestal, a thick round top engraved with a weiqi board, a celadon tea set
  cyl(p, ax, ay, 5.5, 2.8, 3, (a, _t, X, Y) => stoneAt(form(a) - 0.1, X, Y), null);
  cyl(p, ax, ay - 2, 3.5, 1.8, 9, (a, t, X, Y) => stoneAt(form(a) + (t > 0.45 && t < 0.6 ? 0.12 : 0), X, Y), null);
  cyl(
    p,
    ax,
    ay - 11,
    14,
    7,
    3,
    (a, t, X, Y) => stoneAt(form(a) + (t > 0.6 ? 0.14 : t < 0.34 ? -0.22 : -0.04), X, Y),
    (a, b, X, Y) => {
      const dx = a * 14;
      const dy = b * 7;
      const R = Math.round(dx);
      const C = Math.round(dy);
      if (Math.abs(dx) / 9 + Math.abs(dy) / 4.5 < 1) {
        if (md(R + 2 * C, 4) === 0 || md(R - 2 * C, 4) === 0) return M.stone[2]!;
        return M.stone[5]!;
      }
      return stoneAt(capL(a, b) + 0.04, X, Y);
    },
  );
  const T = ay - 14;
  for (const [x, y, c] of [[-3, -1, M.dark[1]!], [1, 0, K.cream[4]!], [-1, 1, M.dark[1]!], [3, -1, K.cream[4]!], [0, -2, M.dark[1]!]] as const) p.px.set(ax + x, T + y, c);
  blob(p.px, ax + 9, T - 1, 2, 1.6, M.jade, 0.62);
  p.px.set(ax + 11, T - 2, M.jade[4]!);
  p.px.set(ax + 9, T - 3, M.jade[2]!);
  p.px.set(ax + 6, T + 1, M.jade[4]!);
  p.px.set(ax + 6, T, M.jade[3]!);
  stool(0.74, -0.24);
  stool(0.2, 0.76);
  return fin4(p, 0.7, 0.7, 0.1);
}

function forge(): Sprite {
  const p = propPix(84, 84, 42, 62);
  // Hearth: brick courses, a coal bed glowing white-hot to cinder, glow on the near brick.
  const pit = [-0.3, 0] as const;
  bx(
    p,
    [-0.7, -0.35, 0.1, 0.35],
    0,
    14,
    (u, v, l) => brk(-0.1)(u, v, l + (l < 0.6 && v > 9 && Math.abs(u / 16 - pit[0]) < 0.3 ? 0.16 : 0)),
    (u, v, l) => {
      const d = Math.hypot((u / 16 - pit[0]) / 0.3, (v / 16 - pit[1]) / 0.24);
      if (d < 1) {
        const h = hash2(u | 0, v | 0);
        const g = 1 - d + (h - 0.5) * 0.35;
        if (h > 0.9) return M.dark[2]!;
        return snap(FIRE, 0.15 + g * 1.05, u | 0, v | 0, 0.2);
      }
      return rock(M.brick, -0.08)(u, v, l);
    },
  );
  // Chimney: brick, soot-blackened toward the top, a dark hood mouth with firelight on its lip, a stone cap.
  bx(p, [-0.6, -0.24, -0.02, 0.24], 14, 42, (u, v, l) => brk(-0.12)(u, v, l - Math.max(0, v - 28) * 0.03 + (v < 19 && l > 0.6 ? 0.1 : 0)), (u, v, l) => snap(M.dark, l, u | 0, v | 0, 0));
  wall(p.px, p.P, 'right', -0.015, -0.15, 0.15, 14, 22, (_u, v) => (v < 15.5 ? FIRE[3]! : v < 17 ? FIRE[1]! : M.dark[0]!), 0.7);
  bx(p, [-0.66, -0.3, 0.04, 0.3], 42, 45, rock(), rock(M.stone, 0.05));
  // tongs hung on the chimney
  const [tx, ty] = p.P(-0.3, 0.25, 34);
  ln(p, [tx, ty], [tx - 1, ty + 10], K.iron[2]!);
  ln(p, [tx + 1, ty], [tx + 2, ty + 10], K.iron[4]!);
  // stump and anvil (body, waist, face, a tapering horn), a hammer leaning on the stump
  const [kx, ky] = p.P(0.42, 0.05, 0);
  cyl(
    p,
    kx,
    ky,
    5,
    2.5,
    7,
    (a, t, X, Y) => snap(M.wood, form(a) - 0.05 + (md(X, 3) === 0 ? -0.14 : 0) + (t < 0.2 ? -0.1 : 0), X, Y, 0),
    (a, b, X, Y) => (md(Math.round(Math.hypot(a, b * 1.2) * 4), 2) === 0 ? K.thatch[3]! : snap(K.thatch, capL(a, b), X, Y, 0)),
  );
  const iron = (u: number, v: number, l: number): RGB => snap(K.iron, l - 0.05, u | 0, v | 0, 0.1);
  bx(p, [0.32, -0.07, 0.52, 0.07], 7, 10, iron, iron, false);
  bx(p, [0.37, -0.04, 0.47, 0.04], 10, 12, iron, iron, false);
  bx(p, [0.25, -0.08, 0.58, 0.08], 12, 15, iron, (u, v, l) => snap(K.iron, l + 0.08, u | 0, v | 0, 0), false);
  const [hx, hy] = p.P(0.58, 0, 14);
  for (let k = 0; k < 5; k++) p.px.rect(hx + k, hy - 1 + (k >> 1), 1, k < 3 ? 2 : 1, K.iron[k < 2 ? 4 : 3]!);
  const [mx, my] = p.P(0.62, 0.32, 0);
  ln(p, [mx, my], [mx - 4, my - 9], M.wood[4]!);
  p.px.rect(mx - 6, my - 11, 5, 2, K.iron[3]!);
  p.px.rect(mx - 6, my - 11, 5, 1, K.iron[5]!);
  // quench tub
  const [qx, qy] = p.P(-0.15, 0.55, 0);
  barrelAt(p, qx, qy, 9, 4);
  blob(p.px, qx, qy - 9, 3.4, 1.3, M.blue, 0.45);
  p.px.set(qx + 1, qy - 10, M.blue[4]!);
  return fin4(p, 0.72, 0.5);
}

function wishingTree(): Sprite {
  const p = propPix(84, 112, 42, 100);
  const { ax, ay } = p;
  cyl(p, ax, ay, 9, 4.5, 3, (a, t, X, Y) => (t < 0.4 && valueNoise(X / 3, 4) > 0.55 ? snap(M.moss, 0.4, X, Y, 0.3) : stoneAt(form(a) + (t > 0.7 ? 0.14 : 0), X, Y)), (a, b, X, Y) => (a * a + b * b < 0.55 ? snap(K.mud, 0.5 + a * 0.1, X, Y, 0.3) : stoneAt(capL(a, b), X, Y)));
  trunk(p, 42, 50, 8, -2);
  fol(p, 42, ay - 62, 30, 20, LEAF, 5);
  // red ribbons with wooden wish plaques, a few ochre ones
  for (let k = 0; k < 16; k++) {
    const x = 20 + ((k * 37) % 46);
    const y = ay - 52 - ((k * 13) % 22);
    const c: Ramp = k % 3 ? M.red : K.ochre;
    const len = 6 + (k % 4);
    p.px.rect(x, y, 1, len, c[3]!);
    p.px.set(x, y - 1, c[1]!);
    if (k % 2 === 0) {
      p.px.rect(x - 1, y + len, 3, 3, M.deck[4]!);
      p.px.rect(x - 1, y + len + 2, 3, 1, M.deck[2]!);
      p.px.set(x, y + len + 1, M.dark[2]!);
    }
  }
  return fin4(p, 0.45, 0.45, 0.12);
}

/** `spin`: how far (columns) the drum's syllables have turned (its loop: kit/propAnim4.ts, 3 steps). */
function prayerWheel(spin = 0): Sprite {
  const p = propPix(40, 72, 20, 62);
  const { ax, ay } = p;
  bx(p, [-0.28, -0.28, 0.28, 0.28], 0, 6, rock(M.stone, 0, true), rock());
  // the drum: lacquer with gold bands, rows of embossed gold syllables, a gloss streak
  cyl(
    p,
    ax,
    ay - 6,
    6.5,
    3,
    22,
    (a, t, X, Y) => {
      const z = Math.round(t * 22);
      if (z === 2 || z === 3 || z === 19 || z === 20) return snap(M.gold, form(a) + 0.1, X, Y, 0);
      if (z > 6 && z < 16 && (z - 7) % 4 < 2 && md(X + spin, 3) === 0 && a > -0.8) return snap(M.gold, form(a) + 0.15, X, Y, 0);
      if (a > 0.32 && a < 0.5 && z > 4 && z < 18) return M.lacquer[5]!;
      return snap(M.lacquer, form(a) - 0.05, X, Y, 0);
    },
    null,
  );
  // gold cap and pearl finial, a handle peg
  cyl(p, ax, ay - 28, 7.5, 3.4, 2, (a, _t, X, Y) => snap(M.gold, form(a), X, Y, 0), (a, b, X, Y) => snap(M.gold, capL(a, b), X, Y, 0));
  finial(p.px, [ax, ay - 32], 8);
  p.px.rect(ax + 7, ay - 12, 2, 1, M.gold[3]!);
  p.px.set(ax + 8, ay - 13, M.gold[4]!);
  return fin4(p, 0.3, 0.3);
}

function sundial(): Sprite {
  const p = propPix(52, 54, 26, 40);
  const { ax, ay } = p;
  bx(p, [-0.4, -0.4, 0.4, 0.4], 0, 4, rock(M.stone, 0, true), rock(M.stone, 0.04));
  cyl(p, ax, ay - 4, 4.5, 2.2, 10, (a, t, X, Y) => stoneAt(form(a) + (t < 0.15 ? -0.12 : 0) + (t > 0.45 && t < 0.55 ? -0.16 : 0) + (t > 0.88 ? 0.12 : 0), X, Y), null);
  // the dial: a tilted stone disc, its edge in shade, a carved hour ring with ticks, a bronze gnomon and its shadow
  const cx = ax;
  const cy = ay - 20;
  for (let y = -8; y <= 8; y++)
    for (let x = -13; x <= 13; x++) {
      const e = (x * x) / 169 + (y * y) / 49;
      if (e > 1) continue;
      p.px.set(cx + x, cy + y + 2, stoneAt(0.3, cx + x, cy + y + 2));
    }
  for (let y = -7; y <= 7; y++)
    for (let x = -12; x <= 12; x++) {
      const e = (x * x) / 144 + (y * y) / 42;
      if (e > 1) continue;
      let l = 0.6 + (x / 12) * 0.14 - (y / 7) * 0.06;
      if (e > 0.78) l += y > 0 ? 0.14 : -0.04;
      if (e > 0.42 && e < 0.52) l = 0.36;
      p.px.set(cx + x, cy + y, stoneAt(l, cx + x, cy + y));
    }
  for (let k = 0; k < 12; k++) {
    const a = (k / 12) * 6.283;
    p.px.set(Math.round(cx + Math.cos(a) * 9.4), Math.round(cy + Math.sin(a) * 5.2), M.dark[2]!);
    if (k % 3 === 0) p.px.set(Math.round(cx + Math.cos(a) * 10.4), Math.round(cy + Math.sin(a) * 5.8), M.stone[1]!);
  }
  ln(p, [cx, cy], [cx - 7, cy + 3], M.stone[1]!);
  ln(p, [cx - 3, cy + 3], [cx + 5, cy - 7], (f) => BRONZE[f > 0.6 ? 5 : f > 0.3 ? 4 : 3]!);
  ln(p, [cx - 2, cy + 3], [cx + 6, cy - 7], (f) => BRONZE[f > 0.5 ? 2 : 1]!);
  return fin4(p, 0.42, 0.42, 0.1);
}

function dovecote(): Sprite {
  const p = propPix(48, 104, 24, 92);
  const { ax, ay } = p;
  bx(p, [-0.12, -0.12, 0.12, 0.12], 0, 3, rock(M.stone, 0, true), rock());
  pole(p.P, p, 0, 0, 0.065, 3, 52, M.wood);
  for (const s of [-1, 1]) ln(p, [ax + s, ay - 42], [ax + s * 8, ay - 52], M.wood[s > 0 ? 4 : 2]!);
  // a plastered house in a timber frame, arched pigeon holes over lit perches
  const holes = (u: number, v: number, l: number): RGB => {
    const lu = u + 4.8;
    if (v < 51 || v > 71 || lu < 0.8 || lu > 8.8) return snap(M.timber, l + 0.15, u | 0, v | 0, 0);
    if (v > 60.5 && v < 61.5) return snap(M.timber, l + 0.1, u | 0, v | 0, 0);
    for (const c of [2.6, 7]) {
      for (const z of [53, 63]) {
        if (Math.abs(lu - c) < 1.3 && v > z + 1.5 && v < z + 5.5 + (Math.abs(lu - c) < 0.6 ? 0.8 : 0)) return M.dark[0]!;
        if (Math.abs(lu - c) < 2 && v > z && v < z + 1.5) return M.wood[4]!;
      }
    }
    return snap(K.wallPlaster, l + 0.1 + (valueNoise(u / 3, v / 4) - 0.5) * 0.12, u | 0, v | 0, 0.2);
  };
  bx(p, [-0.3, -0.3, 0.3, 0.3], 50, 72, holes, (u, v, l) => snap(M.timber, l, u | 0, v | 0), false);
  smallRoof(p, [-0.44, -0.44, 0.44, 0.44], 72, [0, 0, 0, 0], 82, K.greyTile, { fin: 5, shadow: 5 });
  const [ex, ey] = p.P(0.3, 0.1, 53);
  pigeon(p, ex + 2, ey, 1);
  const [rx, ry] = p.P(-0.38, 0.38, 72);
  pigeon(p, rx, ry + 1, -1);
  return fin4(p, 0.16, 0.16, 0.16);
}

function palanquin(): Sprite {
  const p = propPix(112, 100, 56, 74);
  const lac = (u: number, v: number, l: number): RGB => snap(M.lacquer, l + (valueNoise(u / 3, v / 10) - 0.5) * 0.06, u | 0, v | 0, 0.1);
  // the back carrying pole, trestle legs under both poles
  const pz = 13;
  for (const t of [-0.75, 0.75]) for (const y of [-0.44, 0.44]) post(p, t, y, 0, pz, M.timber, 2);
  railLn(p, [-0.98, -0.44], [0.98, -0.44], pz, M.wood);
  // body on a key-pattern plinth, gilt corner posts, a bamboo blind on the side, an embroidered curtain at the door
  bx(p, [-0.42, -0.36, 0.42, 0.36], 3, 10, (u, v, l) => (v > 3.5 && v < 9.5 ? keyBand(4, K.maroon, M.gold)(u, v, l) : snap(M.gold, l, u | 0, v | 0, 0)), lac, false);
  bx(
    p,
    [-0.38, -0.32, 0.38, 0.32],
    10,
    38,
    (u, v, l) => {
      const left = l < 0.6;
      const lo = left ? -6.08 : -5.12;
      const hi = -lo;
      if (u - lo < 1.4 || hi - u < 1.4 || v > 36.4) return snap(M.gold, l + 0.1, u | 0, v | 0, 0);
      if (left) {
        if (Math.abs(u) < 3.6 && v > 21 && v < 33) {
          if (Math.abs(u) > 2.6 || v > 32 || v < 22) return snap(M.gold, l, u | 0, v | 0, 0);
          return (v | 0) % 2 ? K.straw[3]! : K.straw[1]!;
        }
        if (u > hi - 3.4 && u < hi - 2.4 && v > 12) return M.lacquer[5]!;
        if (v > 12 && v < 18 && Math.abs(u) < 4) return Math.abs(u) > 3 || v < 13 || v > 17 ? M.gold[2]! : K.maroon[2]!;
        return lac(u, v, l - 0.04);
      }
      // the door curtain: red cloth in folds, a gold border and roundel, a scalloped hem
      if (Math.abs(u) < 3.8 && v > 12.5 + (md(Math.floor(u), 3) === 0 ? 0.6 : 0)) {
        if (Math.abs(u) > 3) return snap(M.gold, l, u | 0, v | 0, 0);
        const r = Math.hypot(u, (v - 25) * 0.8);
        if (r < 2.2) return r < 1 ? M.gold[4]! : M.gold[2]!;
        const fold = md(Math.floor(u + 0.5), 3);
        return snap(M.red, 0.5 + (fold === 0 ? -0.18 : fold === 1 ? 0.12 : 0) + (v > 33 ? 0.06 : 0), u | 0, v | 0, 0);
      }
      return lac(u, v, l);
    },
    lac,
    false,
  );
  smallRoof(p, [-0.54, -0.46, 0.54, 0.46], 38, [-0.18, 0, 0.18, 0], 49, K.redTile, { tassels: true, fin: 6, shadow: 7 });
  // the front carrying pole, gold ferrules on both poles' ends
  railLn(p, [-0.98, 0.44], [0.98, 0.44], pz, M.wood);
  for (const y of [-0.44, 0.44])
    for (const t of [-0.98, 0.98]) {
      const [x, yy] = p.P(t, y, pz);
      p.px.rect(x - 1, yy - 1, 3, 2, M.gold[3]!);
      p.px.set(x + 1, yy - 1, M.gold[4]!);
    }
  return fin4(p, 0.82, 0.5, 0.12);
}

function teaTrays(): Sprite {
  const p = propPix(92, 62, 46, 42);
  const tray = (cx: number, cy: number, rx: number, ry: number, seed: number): void => {
    for (let y = -Math.ceil(ry) - 1; y <= Math.ceil(ry) + 1; y++)
      for (let x = -Math.ceil(rx) - 1; x <= Math.ceil(rx) + 1; x++) {
        const e = (x * x) / (rx * rx) + (y * y) / (ry * ry);
        if (e > 1) continue;
        const X = cx + x;
        const Y = cy + y;
        if (e > 0.72) p.px.set(X, Y, snap(K.straw, 0.55 + (x / rx) * 0.15 + (y > 0 ? 0.15 : -0.1), X, Y, 0));
        else p.px.set(X, Y, (md(X, 2) ^ md(Y, 2)) ? K.straw[2]! : K.straw[3]!);
      }
    // leaves spread in a low heap: dark under-leaves, lit curled tips toward the right
    for (let y = -Math.ceil(ry * 0.6); y <= Math.ceil(ry * 0.6); y++)
      for (let x = -Math.ceil(rx * 0.62); x <= Math.ceil(rx * 0.62); x++) {
        const e = (x * x) / (rx * rx * 0.38) + (y * y) / (ry * ry * 0.38);
        if (e > 1 || hash2(cx + x + seed, cy + y) < 0.12 + e * 0.3) continue;
        const h = hash2(x * 3 + seed, y * 5);
        p.px.set(cx + x, cy + y - (e < 0.4 ? 1 : 0), TEA[h > 0.85 ? 4 : h > 0.5 ? 3 : h > 0.2 ? 2 : 1]!);
      }
    p.px.rect(cx - Math.round(rx) + 1, cy + Math.round(ry), Math.round(rx * 2) - 1, 1, K.straw[1]!);
  };
  for (const [x, y, s] of [[-0.45, 0.1, 1], [0.45, -0.1, 2]] as const) {
    for (const [dx, dy] of [[-0.3, -0.26], [0.3, -0.26]] as const) post(p, x + dx, y + dy, 0, 12, M.wood, 2);
    railLn(p, [x - 0.33, y - 0.26], [x + 0.33, y - 0.26], 6, BAMBOO);
    const [tx, ty] = p.P(x, y, 13);
    tray(tx, ty, 11, 5.5, s);
    for (const [dx, dy] of [[-0.3, 0.26], [0.3, 0.26]] as const) post(p, x + dx, y + dy, 0, 12, M.wood, 2);
    railLn(p, [x - 0.33, y + 0.26], [x + 0.33, y + 0.26], 6, BAMBOO);
  }
  // a basket of fresh leaves on the ground
  const [bx0, by0] = p.P(0.25, 0.55, 0);
  cyl(p, bx0, by0, 4, 2, 5, (a, t, X, Y) => snap(K.straw, form(a) + (md(X + Y, 2) ? -0.1 : 0) + (t > 0.8 ? 0.1 : 0), X, Y, 0), null);
  blob(p.px, bx0, by0 - 6, 3.6, 1.8, TEA, 0.55);
  return fin4(p, 0.8, 0.5, 0.1);
}

/** `rot`: the wheel's turn in degrees (its loop: kit/propAnim4.ts; spokes and paddles repeat every 90°). */
function waterWheel(rot = 0): Sprite {
  const p = propPix(72, 92, 36, 72);
  const cy = p.ay - 34;
  for (let a = 0; a < 360; a += 3) for (const R of [26, 25, 8, 7]) p.px.set(p.ax + Math.cos((a * Math.PI) / 180) * R * 0.6, cy + Math.sin((a * Math.PI) / 180) * R, snap(M.wood, 0.45 + Math.cos((a * Math.PI) / 180) * 0.2, a, R, 0.2));
  for (let a = rot; a < 360 + rot; a += 45) for (let r = 8; r < 26; r++) p.px.set(p.ax + Math.cos((a * Math.PI) / 180) * r * 0.6, cy + Math.sin((a * Math.PI) / 180) * r, M.wood[2]!);
  for (let a = rot; a < 360 + rot; a += 30) {
    const x = p.ax + Math.cos((a * Math.PI) / 180) * 26 * 0.6;
    const y = cy + Math.sin((a * Math.PI) / 180) * 26;
    p.px.rect(x - 1, y - 1, 3, 3, M.wood[4]!);
  }
  blob(p.px, p.ax, cy, 3, 3, K.iron, 0.6);
  bx(p, [-0.9, -0.1, 0.9, 0.1], 0, 3, rock(M.stone, 0, true), rock());
  blob(p.px, p.ax, p.ay + 1, 16, 4, M.blue, 0.4);
  for (let k = 0; k < 6; k++) p.px.set(p.ax - 10 + k * 4, p.ay + (k & 1), M.linen[4]!);
  return fin4(p, 0.9, 0.12, 0.1);
}

/** `k`: the loop's frame (propAnim4): the pigs root at the trough in turn. */
function pigPen(k = 0): Sprite {
  const p = propPix(104, 72, 52, 50);
  // the floor: trodden mud, a wet wallow with sky glints, straw in the back corner
  for (let Y = 0; Y < p.px.h; Y++)
    for (let X = 0; X < p.px.w; X++) {
      const a = (X + 0.5 - p.ax) / 16;
      const b = (Y + 0.5 - p.ay) / 8;
      const x = (a + b) / 2;
      const y = (b - a) / 2;
      if (Math.abs(x) > 0.96 || Math.abs(y) > 0.96) continue;
      const wet = valueNoise(x * 2.2 + 4, y * 2.2) - Math.hypot(x - 0.25, y - 0.3) * 0.35;
      if (wet > 0.42) p.px.set(X, Y, hash2(X, Y) > 0.96 ? M.blue[3]! : snap(K.mud, 0.18, X, Y, 0.2));
      else if (x < -0.45 && y < -0.2 && hash2(X >> 1, Y) > 0.35) p.px.set(X, Y, K.straw[2 + (hash2(X, Y) > 0.6 ? 1 : 0)]!);
      else p.px.set(X, Y, snap(K.mud, 0.45 + (valueNoise(x * 6, y * 6) - 0.5) * 0.3 + (hash2(X, Y) > 0.93 ? -0.2 : 0), X, Y, 0.3));
    }
  // back fences
  const fence = (a: readonly [number, number], b: readonly [number, number]): void => {
    for (const z of [6, 12]) railLn(p, a, b, z);
  };
  fence([-1, -1], [1, -1]);
  fence([-1, -1], [-1, 1]);
  for (const [x, y] of [[-1, -1], [0, -1], [1, -1], [-1, 0], [-1, 1]] as const) post(p, x, y, 0, 16, M.wood, 3);
  // trough of slop along the back, pigs at it and in the wallow
  bx(
    p,
    [-0.75, -0.82, -0.05, -0.6],
    0,
    5,
    planks(M.wood, 0, 4, false),
    (u, v, l) => (Math.abs(v + 11.4) < 1.6 && u > -11 && u < -1.6 ? snap(K.ochre, 0.42 + (hash2(u | 0, v | 0) > 0.7 ? 0.2 : 0), u | 0, v | 0, 0.2) : planks(M.wood, 0.05)(u, v, l)),
  );
  const [g1x, g1y] = p.P(-0.38, -0.38, 0);
  pig(p, g1x, g1y, -1, 1, [0, 1, 1, 0, 0, 0][k % 6]!);
  const [g3x, g3y] = p.P(0.45, -0.35, 0);
  pig(p, g3x, g3y, -1, 0.7, [0, 0, 0, 1, 0, 0][k % 6]!);
  const [g2x, g2y] = p.P(0.2, 0.3, 0);
  pig(p, g2x, g2y, 1, 1, [1, 0, 0, 0, 0, 1][k % 6]!);
  // front fences, a gate on the right side
  fence([-1, 1], [1, 1]);
  for (const [a, b] of [[[1, -1], [1, -0.3]], [[1, 0.3], [1, 1]]] as const) fence(a, b);
  for (const [x, y] of [[0, 1], [1, 1], [1, -0.3], [1, 0.3]] as const) post(p, x, y, 0, 16, M.wood, 3);
  const g0 = p.P(1, -0.3, 4);
  const g1 = p.P(1, 0.3, 13);
  ln(p, g0, g1, M.wood[3]!);
  railLn(p, [1, -0.3], [1, 0.3], 5);
  railLn(p, [1, -0.3], [1, 0.3], 12);
  return fin4(p, 1, 1, 0.1, 0.12);
}

function woodShed(): Sprite {
  const p = propPix(108, 84, 54, 62);
  // back posts, then the stack: bark on top and the right end, end grain rings on the near face
  for (const x of [-0.9, 0.9]) post(p, x, -0.42, 0, 44, M.wood, 3);
  const bark = (u: number, v: number, l: number): RGB => snap(M.wood, l - 0.12 + (md(Math.floor(u * 0.5 + v * 0.2), 3) === 0 ? -0.14 : 0) + (hash2(u | 0, v | 0) > 0.9 ? 0.12 : 0), u | 0, v | 0, 0.1);
  bx(p, [-0.82, -0.32, 0.38, 0.3], 0, 22, bark, bark);
  for (let r = 0; r < 5; r++)
    for (let c = 0; c < 5; c++) {
      const x = -0.68 + c * 0.3 + (r & 1) * 0.15;
      if (x > 0.3) continue;
      const [ex, ey] = p.P(x, 0.31, 3 + r * 4.4);
      const R = 3 + hash2(r, c) * 0.3;
      for (let b = -3; b <= 3; b++)
        for (let a = -3; a <= 3; a++) {
          const d = Math.hypot(a, b * 1.05);
          if (d > R) continue;
          const lit = (a - b > 1 ? 0.12 : a - b < -2 ? -0.1 : 0) - (r > 3 ? 0.12 : 0);
          const c2 = d > R - 0.9 ? M.wood[1]! : d < 0.8 ? K.thatch[2]! : md(Math.round(d), 2) ? snap(K.thatch, 0.62 + lit, a, b, 0) : snap(K.thatch, 0.8 + lit, a, b, 0);
          p.px.set(ex + a, ey + b, c2);
        }
      if (hash2(c, r + 4) > 0.75) p.px.set(ex, ey - 1, M.wood[1]!);
    }
  // front posts and the lean-to thatch: strands down the slope, a dark band per course, a ragged fringe
  for (const x of [-0.9, 0.9]) post(p, x, 0.42, 0, 32, M.wood, 3);
  const a0 = p.P(-1.05, 0.52, 33);
  const a1 = p.P(1.05, 0.52, 33);
  const b1 = p.P(1.05, -0.52, 45);
  const b0 = p.P(-1.05, -0.52, 45);
  quad(p.px, a0, a1, b1, b0, (s, t, S, T) => {
    void S;
    void T;
    const X = Math.round(s);
    const tt = t;
    const strand = hash2(Math.floor(X / 1.5), 5);
    let l = 0.58 + (strand - 0.5) * 0.24 + (hash2(X, Math.floor(tt / 2)) > 0.9 ? -0.15 : 0);
    if (md(Math.floor(tt + Math.abs(md(X, 6) - 3) * 0.5), 5) === 4) l -= 0.2;
    if (t < 1.2) l += 0.16;
    return snap(K.thatch, l, X, Math.round(tt), 0.15);
  });
  for (let k = 0; k <= 64; k++) {
    const f = k / 64;
    const x = Math.round(a0[0] + (a1[0] - a0[0]) * f);
    const y = Math.round(a0[1] + (a1[1] - a0[1]) * f);
    const n = 1 + Math.floor(hash2(k, 3) * 3);
    for (let d = 1; d <= n; d++) p.px.set(x, y + d, K.thatch[Math.max(0, 3 - d)]!);
  }
  // the chopping block with an axe, split billets on the ground
  const [kx, ky] = p.P(0.72, 0.18, 0);
  cyl(
    p,
    kx,
    ky,
    4.5,
    2.3,
    6,
    (a, t, X, Y) => snap(M.wood, form(a) - 0.05 + (md(X, 3) === 0 ? -0.14 : 0) + (t < 0.2 ? -0.1 : 0), X, Y, 0),
    (a, b, X, Y) => (md(Math.round(Math.hypot(a, b * 1.2) * 4), 2) === 0 ? K.thatch[3]! : snap(K.thatch, capL(a, b), X, Y, 0)),
  );
  ln(p, [kx - 1, ky - 9], [kx + 6, ky - 17], M.wood[4]!);
  ln(p, [kx - 1, ky - 8], [kx + 6, ky - 16], M.wood[2]!);
  p.px.rect(kx - 3, ky - 11, 4, 3, K.iron[3]!);
  p.px.rect(kx - 3, ky - 11, 4, 1, K.iron[5]!);
  for (const [dx, dy] of [[-9, 3], [-4, 5], [10, 4]] as const) {
    p.px.rect(kx + dx, ky + dy, 4, 2, K.thatch[4]!);
    p.px.rect(kx + dx, ky + dy + 1, 4, 1, M.wood[2]!);
  }
  return fin4(p, 1, 0.5, 0.12);
}

/** `swing`: the gong's sideways swing on its cords (px; propAnim4 loops it). */
function gongStand(swing = 0): Sprite {
  const p = propPix(76, 92, 38, 76);
  const { ax, ay } = p;
  const lac = (u: number, v: number, l: number): RGB => snap(M.lacquer, l, u | 0, v | 0, 0.1);
  for (const x of [-0.5, 0.5]) column(p.px, p.P, x, 0, 0.055, 0, 52, M.lacquer);
  bx(p, [-0.5, -0.04, 0.5, 0.04], 9, 11, lac, lac, false);
  // the top beam: a key-pattern band, scroll-cut ends, a grey tile cap, tassels at the ends
  bx(p, [-0.66, -0.06, 0.66, 0.06], 50, 56, (u, v, l) => (l < 0.6 && Math.abs(u) < 8.5 ? keyBand(50, K.maroon, M.gold)(u, v, l) : lac(u, v, l)), lac, false);
  for (const s of [-1, 1]) {
    const [ex, ey] = p.P(s * 0.66, 0, 56);
    p.px.set(ex + s, ey - 1, M.lacquer[4]!);
    p.px.set(ex + s * 2, ey - 2, M.lacquer[3]!);
    p.px.set(ex + s * 2, ey - 3, M.gold[3]!);
    tassel(p.px, p.P(s * 0.62, 0, 50), 9);
  }
  smallRoof(p, [-0.74, -0.14, 0.74, 0.14], 56, [-0.52, 0, 0.52, 0], 62, K.greyTile);
  // the gong on two cords: a rolled rim, a dished field, a raised ring and a bright boss
  const gx = ax + swing;
  const gy = ay - 30;
  for (const s of [-1, 1]) ln(p, [gx + s * 5, gy - 10], [gx + s * 7, ay - 50], M.dark[2]!);
  for (let y = -12; y <= 12; y++)
    for (let x = -11; x <= 11; x++) {
      const r = Math.hypot(x / 11, y / 12);
      if (r > 1) continue;
      const ang = Math.atan2(-y, x);
      const lit = Math.cos(ang - Math.PI / 4);
      let l = 0.52 - r * 0.08 + lit * 0.06;
      if (r > 0.86) l = 0.42 + lit * 0.26;
      else if (r > 0.5 && r < 0.62) l = 0.5 + lit * 0.3;
      else if (r < 0.26) l = 0.66 + lit * 0.22;
      if (r < 0.12 && x > 0 && y < 0) l = 1;
      p.px.set(gx + x, gy + y, snap(BRONZE, l, gx + x, gy + y, 0.2));
    }
  // the mallet hung on the right post: a wrapped red head, a gold band
  const [mx, my] = p.P(0.5, 0, 30);
  ln(p, [mx + 3, my - 4], [mx + 3, my + 8], M.wood[3]!);
  blob(p.px, mx + 3, my + 11, 2.6, 2.8, M.red, 0.6);
  p.px.rect(mx + 1, my + 9, 5, 1, M.gold[3]!);
  return fin4(p, 0.62, 0.2, 0.12);
}

function willowTree(): Sprite {
  const p = propPix(96, 120, 48, 108);
  trunk(p, 46, 52, 7, 6);
  fol(p, 52, p.ay - 66, 30, 12, WILLOW, 9);
  for (let k = 0; k < 28; k++) {
    const x = 24 + k * 2 + (k & 1);
    const len = 24 + ((k * 7) % 5) * 5;
    for (let z = 0; z < len; z++) p.px.set(x + Math.round(Math.sin(z * 0.2 + k) * 1.2), p.ay - 62 + z, snap(WILLOW, 0.3 + (z / len) * 0.4 + hash2(k, z) * 0.2, x, z, 0.3));
  }
  return fin4(p, 0.25, 0.25, 0.14);
}
function plumTree(): Sprite {
  const p = propPix(80, 100, 40, 90);
  trunk(p, 38, 32, 6, -4);
  for (let z = 22; z < 50; z++) p.px.rect(p.ax - 8 + Math.round((z - 22) * 0.7), p.ay - z, 3, 1, M.wood[2 + (z & 1)]!);
  fol(p, 36, p.ay - 58, 24, 16, BLOOM, 21);
  for (let k = 0; k < 14; k++) p.px.set(14 + ((k * 29) % 52), p.ay - 40 - ((k * 11) % 36), M.linen[4]!);
  blob(p.px, p.ax, p.ay + 1, 12, 3, K.mud, 0.35);
  for (let k = 0; k < 9; k++) p.px.set(p.ax - 12 + ((k * 7) % 24), p.ay + ((k * 3) % 3) - 1, BLOOM[3]!);
  return fin4(p, 0.25, 0.25, 0.14);
}
function pineTree(): Sprite {
  const p = propPix(70, 120, 35, 108);
  trunk(p, 35, 60, 6, 2);
  for (let t = 0; t < 5; t++) fol(p, 35 + (t & 1 ? 5 : -4), p.ay - 40 - t * 14, 24 - t * 3, 6, PINEG, 30 + t);
  fol(p, 36, p.ay - 110, 8, 5, PINEG, 77);
  return fin4(p, 0.22, 0.22, 0.14);
}
function bambooArch(): Sprite {
  const p = propPix(90, 78, 45, 60);
  for (const x of [-1, 1]) bx(p, [x - 0.16, -0.1, x + 0.16, 0.1], 0, 2, rock(M.stone, 0, true), rock());
  for (const x of [-1, 1]) for (const dx of [-0.07, 0.07]) pole(p.P, p, x + dx, 0, 0.04, 2, 52, BAMBOO);
  for (let k = 0; k <= 32; k++) {
    const t = k / 32;
    const [x, y] = p.P(-1 + t * 2, 0, 50 + Math.sin(t * Math.PI) * 10);
    p.px.rect(x, y, 2, 2, snap(LEAF, 0.5, k, 0, 0.3));
  }
  for (const x of [-1, 1])
    for (let z = 8; z < 50; z += 9) {
      const [a, b] = p.P(x, 0, z);
      p.px.rect(a - 3, b, 7, 1, M.wood[1]!);
    }
  for (const x of [-0.5, 0.5]) {
    const [a, b] = p.P(x, 0, 58);
    p.px.rect(a, b, 1, 4, M.gold[2]!);
    blob(p.px, a, b + 7, 3, 3.5, M.red, 0.65);
    p.px.rect(a - 2, b + 4, 5, 1, M.gold[3]!);
    p.px.rect(a - 2, b + 10, 5, 1, M.gold[3]!);
  }
  return fin4(p, 1.05, 0.12, 0.1);
}

/** Ground decals: walkable, painted from tile coordinates, sorted at the far corner (carpet.ts convention). */
function decal(lx: number, ly: number, tex: (x: number, y: number, X: number, Y: number) => RGB | null): FlatSprite {
  const pad = 2;
  const W = 16 * (lx + ly) + pad * 2;
  const H = 8 * (lx + ly) + pad * 2;
  const ox = 16 * ly + pad;
  const oy = pad;
  const p = propPix(W, H, ox + 8 * (lx - ly), oy + 4 * (lx + ly));
  for (let Y = 0; Y < H; Y++)
    for (let X = 0; X < W; X++) {
      const a = (X + 0.5 - ox) / 16;
      const b = (Y + 0.5 - oy) / 8;
      const x = (a + b) / 2;
      const y = (b - a) / 2;
      if (x < 0 || y < 0 || x >= lx || y >= ly) continue;
      const c = tex(x, y, X, Y);
      if (c) p.px.set(X, Y, c);
    }
  return { ...fin(p), flat: [lx, ly] };
}
const ragged = (x: number, y: number, lx: number, ly: number): number => Math.min(x, lx - x, y, ly - y) * 5 - 0.6 - hash2((x * 7) | 0, (y * 7) | 0) * 1.6;
function cobblePatch(): FlatSprite {
  return decal(2, 2, (x, y, X, Y) => {
    if (ragged(x, y, 2, 2) < 0) return null;
    const cx = Math.floor(x * 3 + (Math.floor(y * 3) & 1) * 0.5);
    const cy = Math.floor(y * 3);
    const fx = md(x * 3 + (cy & 1) * 0.5, 1);
    const fy = md(y * 3, 1);
    const edge = Math.abs(fx - 0.5) > 0.42 || Math.abs(fy - 0.5) > 0.42;
    if (edge) return M.stone[1]!;
    // each cobble lit on its far-right rim, shaded on its near-left
    const rim = fx > 0.78 || fy < 0.2 ? 0.14 : fx < 0.2 || fy > 0.8 ? -0.12 : 0;
    return snap(M.stone, 0.45 + hash2(cx, cy) * 0.3 + rim, X, Y, 0.15);
  });
}
function dirtTrack(): FlatSprite {
  return decal(3, 2, (x, y, X, Y) => {
    if (ragged(x, y, 3, 2) < 0.3) return null;
    const rut = Math.abs(y - 0.7) < 0.06 || Math.abs(y - 1.3) < 0.06;
    const lip = Math.abs(y - 0.62) < 0.03 || Math.abs(y - 1.22) < 0.03;
    if (rut) return K.mud[1]!;
    if (lip) return K.mud[4]!;
    return snap(K.mud, 0.5 + (hash2(X >> 1, Y) - 0.5) * 0.3, X, Y, 0.3);
  });
}
function grainMat(): FlatSprite {
  return decal(2, 2, (x, y, X, Y) => {
    const e = Math.min(x, 2 - x, y, 2 - y);
    if (e < 0.1) return e < 0.04 ? M.dark[1]! : M.wood[3]!;
    // grain raked into furrows, a heap in the far corner
    const heap = 0.55 - Math.hypot(x - 0.6, y - 0.6);
    const furrow = md(Math.floor((x + y) * 6), 2) ? 0.06 : -0.06;
    return snap(K.straw, 0.55 + furrow + (heap > 0 ? heap * 0.6 : 0) + (hash2(X, Y) > 0.85 ? 0.15 : 0), X, Y, 0.2);
  });
}
function ditch(): FlatSprite {
  return decal(3, 1, (x, y, X, Y) => {
    const e = Math.abs(y - 0.5);
    if (e < 0.2) return snap(M.blue, 0.4 + (md(x * 4 + Math.sin(x * 3), 1) < 0.1 ? 0.3 : 0) + (y < 0.36 ? -0.12 : 0), X, Y, 0.2);
    if (e < 0.3) return snap(M.stone, 0.4 + hash2(X, Y) * 0.3 + (y > 0.5 ? 0.12 : -0.06), X, Y, 0.2);
    return null;
  });
}

export const MAKERS4 = {
  noticeBoard,
  waterTrough,
  millstone,
  scarecrow,
  henCoop: () => henCoop(),
  farmGateX: () => farmGate('x'),
  farmGateY: () => farmGate('y'),
  stoneTable,
  forge,
  wishingTree,
  prayerWheel: () => prayerWheel(),
  sundial,
  dovecote,
  palanquin,
  teaTrays,
  waterWheel: () => waterWheel(),
  pigPen: () => pigPen(),
  woodShed,
  gongStand: () => gongStand(),
  willowTree,
  plumTree,
  pineTree,
  bambooArch,
  cobblePatch,
  dirtTrack,
  grainMat,
  ditch,
} satisfies Record<string, () => Sprite>;
export type PropId4 = keyof typeof MAKERS4;
/** Frame makers for the props that move at their source (kit/propAnim4.ts): frame `k` of `n`. */
export const LOOPS4 = {
  waterWheel: (k: number, n: number) => waterWheel((k / n) * 90),
  prayerWheel: (k: number) => prayerWheel(k % 3),
  henCoop: (k: number) => henCoop(k),
  pigPen: (k: number) => pigPen(k),
  gongStand: (k: number) => gongStand([0, 1, 1, 0, -1, -1][k % 6]!),
} satisfies Partial<Record<PropId4, (k: number, n: number) => Sprite>>;

/** Thai labels for the review sheet. */
export const LABELS4: Record<PropId4, string> = {
  noticeBoard: 'ป้ายประกาศ', waterTrough: 'รางน้ำม้า', millstone: 'ครกหิน', scarecrow: 'หุ่นไล่กา', henCoop: 'เล้าไก่',
  farmGateX: 'ประตูไร่ X', farmGateY: 'ประตูไร่ Y', stoneTable: 'โต๊ะหิน', forge: 'เตาตีเหล็ก', wishingTree: 'ต้นอธิษฐาน',
  prayerWheel: 'กงล้อมนตร์', sundial: 'นาฬิกาแดด', dovecote: 'เล้านกพิราบ', palanquin: 'เกี้ยว', teaTrays: 'ตะแกรงตากชา',
  waterWheel: 'กังหันน้ำ', pigPen: 'คอกหมู', woodShed: 'เพิงฟืน', gongStand: 'ฆ้องตั้ง', willowTree: 'ต้นหลิว',
  plumTree: 'ต้นบ๊วย', pineTree: 'ต้นสน', bambooArch: 'ซุ้มไผ่', cobblePatch: 'ลานหินกรวด', dirtTrack: 'ทางดินรอยล้อ',
  grainMat: 'เสื่อตากข้าว', ditch: 'คูน้ำ',
};
